<template>
  <div
    v-if="gwpConfig.active && cartCount > 0"
    class="mini-cart__gwp"
  >
    <div class="accordion-item border-b">
      <h3>
        <button
          :aria-expanded="showGWP ? 'true' : 'false'"
          aria-controls="gwp-panel"
          id="gwp-header"
          class="flex items-center justify-between w-full text-left py-4 cursor-pointer"
          :disabled="!isEligibleForGWP"
          @click="handleGWPToggle"
          @keydown="handleKeyDown"
        >
         <div class="flex items-center gap-2 justify-between w-[calc(100%-1rem)] text-left">

            <span class="flex flex-1 justify-between items-center flex-wrap gap-2">
              <span class="flex gap-2 relative items-center">
                <span v-if="showAccordionIcons" class="mini-cart__util-icon">
                  <MiniCartIconGift />
                </span>
                {{ gwpConfig.heading }}
              </span>
              <span>
                <span v-if="hasSelectedGWP" class="text-xs mini-cart__truncated-preview-text">
                  ({{ addedGWP.length }} of {{ gwpConfig.maxSelections }} {{ gwpConfig.maxSelections > 1 ? 'gifts' :
                  'gift' }} selected)
                </span>
                <span v-else-if="isEligibleForGWP && gwpConfig.maxSelections > 1"
                  class="text-xs mini-cart__truncated-preview-text">
                  (Select up to {{ gwpConfig.maxSelections }} gifts)
                </span>
                <span v-if="!isEligibleForGWP" class="mini-cart__truncated-preview-text max-w-full!">
                  {{ amountAwayMessage }}
                </span>

                <span v-else-if="!hasSelectedGWP" class="mini-cart__truncated-preview-text">
                  {{ gwpConfig.eligibleText }}
                </span>
              </span>
            </span>
          </div>
          <svg
            :class="showGWP ? 'rotate-180' : 'rotate-0'"
            class="w-4 h-4 transition-transform duration-200"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            ></path>
          </svg>
        </button>
      </h3>
      <button
        v-if="showGWP"
        @click="handleGWPToggle"
        class="w-full h-full fixed inset-0 mini-cart__blocker"
        style="
          background-color: var(--global-mask-color);
          backdrop-filter: var(--global-mask-blur);
        "
      >
        <span class="sr-only">close</span>
      </button>
      <div
        id="gwp-panel"
        aria-labelledby="gwp-header"
        class="overflow-hidden transition-all duration-300 ease-in-out mini-cart__slide-up-panel"
        :class="showGWP ? 'max-h-[600px] opacity-100 panel_active' : 'panel_disabled'"
        @keydown="handlePanelKeyDown"
      >
        <section
          class="mini-cart__gwp-container relative transition-all duration-300 ease-in-out pb-4"
          :class="showGWP ? 'opacity-100' : 'opacity-0'"
        >
          <div>
            <p
              v-if="hasSelectedGWP && gwpConfig.selectedMessage"
              class="mb-3 text-green-600 font-medium"
            >
              {{ gwpConfig.selectedMessage }}
            </p>
            <p
              v-else-if="isEligibleForGWP"
              class="mb-3"
            >
              {{ gwpConfig.description }}
            </p>
          </div>

          <div
            v-if="isEligibleForGWP && availableGWPProducts.length"
            class="mini-cart__gwp-inner relative"
            :data-gwp-product-count="availableGWPProducts.length"
          >
            <Swiper
              :slides-per-view="2"
              :space-between="10"
              :observer="true"
              :observe-parents="true"
              :modules="modules"
              :threshold="2"
              :navigation="{
                nextEl: '.gwp-swiper-button-next',
                prevEl: '.gwp-swiper-button-prev',
              }"
              class="mini-cart__gwp-swiper"
            >
              <SwiperSlide
                v-for="product in availableGWPProducts"
                :key="product.id"
                class="!h-auto"
              >
                <div
                  class="mini-cart__gwp-item border flex items-center gap-3 p-4 h-full"
                  :class="{
                    'border-green-500 bg-green-50': productAlreadyAdded(product.id),
                    'opacity-50': hasSelectedGWP && !productAlreadyAdded(product.id),
                  }"
                >
                  <div class="mini-cart__gwp-fig w-20 h-20">
                    <img
                      :srcset="getCroppedImageUrl(product.featured_image, [120, 160, 240, 320])"
                      :src="getCroppedImageUrl(product.featured_image, 160)"
                      :alt="product.title"
                      class="w-full h-full object-cover"
                      width="80"
                      height="80"
                      loading="lazy"
                    />
                  </div>

                  <div class="mini-cart__gwp-meta text-xs flex-1 flex flex-col justify-between text-center gap-2">
                    <h2
                      class="mini-cart__meta-title text-wrap"
                      :title="product.title"
                    >
                      {{ product.title }}
                    </h2>

                    <span class="mini-cart__meta-price text-xs line-through text-gray-500">
                      {{ currencySymbol }}{{ (product.price / 100).toFixed(2) }}
                    </span>

                    <form @submit.prevent="addGWPToCart(product)" class="flex flex-col gap-2">
                      <!-- Variant Selector -->
                      <select
                        v-if="!(product.variants.length === 1 && product.variants[0].title === 'Default Title')"
                        name="id"
                        v-model="selectedVariantIds[product.id]"
                        :id="`gwpProductSelect--${product.id}`"
                        class="w-full transition-opacity mini-cart__select"
                        :class="{ 'opacity-50': isAnyLoading() && !isLoading(product.id) }"
                        :disabled="isLoading(product.id) || isAnyLoading() || hasSelectedGWP"
                      >
                        <option
                          v-for="variant in product.variants"
                          :key="variant.id"
                          :value="variant.id"
                          :disabled="!variant.available"
                        >
                          {{ variant.title }}
                        </option>
                      </select>
                      
                      <button
                        v-if="!productAlreadyAdded(product.id)"
                        type="submit"
                        name="add"
                        class="mini-cart__gwp-add-btn mini-cart__button"
                        :disabled="isLoading(product.id) || isAnyLoading() || hasSelectedGWP"
                        :class="{ 'cursor-not-allowed': isLoading(product.id) || hasSelectedGWP }"
                      >
                        {{ isLoading(product.id) ? gwpConfig.addingText : gwpConfig.addButton }}
                      </button>

                      <div
                        v-else
                        class="flex flex-col gap-2"
                      >
                        <p class="text-xs text-center text-green-600">
                          ✓ Gift Added
                        </p>
                        <button
                          type="button"
                          @click="removeGWPFromCart(product.id)"
                          class="mini-cart__gwp-remove-btn text-xs py-1 px-2 border border-red-500 text-red-500 hover:bg-red-50 transition-colors"
                          :disabled="isLoading(product.id) || isAnyLoading()"
                          :class="{ 'cursor-not-allowed opacity-50': isLoading(product.id) || isAnyLoading() }"
                        >
                          {{ gwpConfig.removingText }}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </SwiperSlide>
            </Swiper>
            <div class="gwp-swiper-button-prev swiper-button-prev"></div>
            <div class="gwp-swiper-button-next swiper-button-next"></div>
          </div>

          <p
            v-else-if="hasSelectedGWP && !availableGWPProducts.length"
            class="text-xs text-green-600 font-medium"
          >
            You've selected your free {{ gwpConfig.maxSelections > 1 ? 'gifts' : 'gift' }}! {{ gwpConfig.maxSelections > 1 ? `Maximum ${gwpConfig.maxSelections} gifts` : 'Only one gift' }} per order.
          </p>

          <p
            v-else-if="!availableGWPProducts.length && isEligibleForGWP"
            class="text-xs text-gray-500"
          >
            No gifts available at this time.
          </p>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useCartLoading } from './composables/useCartLoading';
import { useAccordion } from './composables/useAccordion';
import { getCroppedImageUrl } from '../helpers/get-cropped-image-url.js';
import MiniCartIconGift from './mini-cart--icon-gift.vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';

const debounce = (fn, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

export default {
  name: 'GWPComponent',
  components: {
    MiniCartIconGift,
    Swiper,
    SwiperSlide,
  },
  props: {
    cartTotal: {
      type: Number,
      required: true,
    },
    cartCount: {
      type: Number,
      required: true,
    },
    currencySymbol: {
      type: String,
      required: true,
      default: () => window.miniCartCustomSettings?.currencySymbol || '$',
    },
  },
  setup(props, { emit }) {
    // Swiper modules
    const modules = [Navigation];
    const { setLoading, clearLoading, isLoading, isAnyLoading } = useCartLoading();

    // Load config directly from window (set by Liquid template)
    const gwpConfig = ref(window.gwpConfig || { active: false, products: [] });
    const addedGWP = ref([]);
    const cartItems = ref([]);
    const selectedVariantIds = ref({});
    // const showGWP = ref(false);
    const { isOpen: showGWP, toggle: toggleGWP, close: closeGWP } = useAccordion('gwp');
    const recentlyAddedGWP = ref(null);
    const showAccordionIcons = ref(window.miniCartCustomSettings?.showAccordionIcons ?? true);

    // Check if cart meets minimum for GWP
    const isEligibleForGWP = computed(() => {
      return props.cartTotal >= gwpConfig.value.minimumPurchase;
    });

    // Amount needed to reach GWP threshold
    const amountAwayMessage = computed(() => {
      const amountNeeded = ((gwpConfig.value.minimumPurchase - props.cartTotal) / 100).toFixed(2);
      return gwpConfig.value.amountAwayText.replace(/{amount}/g, `${props.currencySymbol}${amountNeeded}`);
    });

    // Filter out already added GWP products
    const availableGWPProducts = computed(() => {
      const maxSelections = gwpConfig.value.maxSelections || 1;
      
      // If max selections reached, only show added products
      if (addedGWP.value.length >= maxSelections) {
        return gwpConfig.value.products.filter(product => addedGWP.value.includes(product.id));
      }
      
      // Initialize selected variant IDs for each product
      const products = gwpConfig.value.products;
      products.forEach(product => {
        if (!selectedVariantIds.value[product.id] && product.variants?.length > 0) {
          // Set default to first available variant
          const firstAvailable = product.variants.find(v => v.available);
          if (firstAvailable) {
            selectedVariantIds.value[product.id] = firstAvailable.id;
          }
        }
      });
      
      return products;
    });

    // Check if ANY GWP product has been selected (respect maxSelections)
    const hasSelectedGWP = computed(() => {
      const maxSelections = gwpConfig.value.maxSelections || 1;
      return addedGWP.value.length >= maxSelections;
    });

    // Check if specific product is already added
    const productAlreadyAdded = (productId) => addedGWP.value.includes(productId);

    // Calculate remaining gift selections
    const remainingSelections = computed(() => {
      const maxSelections = gwpConfig.value.maxSelections || 1;
      return Math.max(0, maxSelections - addedGWP.value.length);
    });

    // Check if product is a GWP product
    const isGWPProduct = (productId) => {
      return gwpConfig.value.products.some(product => product.id === productId);
    };

    // Check if item is a free gift (price is 0 OR has _gwp property)
    const isFreeGift = (item) => {
      // Check if item has _gwp property
      const hasGWPProperty = item.properties && item.properties._gwp === 'true';

      // Check if final price is 0 (discounted_price or final_line_price)
      const isFreePrice = item.final_line_price === 0 ||
        item.discounted_price === 0 ||
        item.price === 0;

      // Check if it's a GWP product by ID
      const isGWPById = isGWPProduct(item.product_id || item.id);

      return hasGWPProperty || (isFreePrice && isGWPById);
    };

    // Fetch cart and update GWP tracking
    const getCartAndUpdateGWP = async () => {
      try {
        const response = await fetch(`${Shopify.routes.root}cart.js`);
        if (!response.ok) throw new Error('Failed to fetch cart');
        const cartData = await response.json();
        cartItems.value = cartData.items;

        // Track ALL free gifts in cart (GWP products with price 0)
        addedGWP.value = cartData.items
          .filter(item => isFreeGift(item))
          .map(item => item.product_id || item.id);
        
        // Update selectedVariantIds to match what's in the cart
        cartData.items.forEach(item => {
          if (isFreeGift(item)) {
            const productId = item.product_id || item.id;
            selectedVariantIds.value[productId] = item.variant_id;
          }
        });
        
        emit('cart-updated', cartData);
      } catch (error) {
        console.error('Error fetching cart:', error);
      }
    };

    // Remove item from cart
    const removeFromCart = async (itemKey) => {
      try {
        const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: itemKey, quantity: 0 }),
        });
        if (!response.ok) throw new Error('Failed to remove item');
        await getCartAndUpdateGWP();
      } catch (error) {
        console.error('Error removing item:', error);
      }
    };

    const removeGWPFromCart = async (productId) => {
      try {
        setLoading(productId);
        const gwpItem = cartItems.value.find(item => 
          (item.product_id === productId || item.id === productId) && isFreeGift(item)
        );

        if (!gwpItem) {
          console.warn('GWP product not found in cart:', productId);
          return;
        }
        await removeFromCart(gwpItem.key);
        addedGWP.value = addedGWP.value.filter(id => id !== productId);
        emit('gwp-removed', productId);

      } catch (error) {
        console.error('Error removing GWP from cart:', error);
      } finally {
        clearLoading();
      }
    };

    // Add GWP product to cart
    const addGWPToCart = async (product) => {
      const maxSelections = gwpConfig.value.maxSelections || 1;
      
      // Check if max selections reached
      if (addedGWP.value.length >= maxSelections || isAnyLoading()) {
        console.warn(`GWP limit reached - only ${maxSelections} gift${maxSelections > 1 ? 's' : ''} allowed per order`);
        return;
      }

      // Use selected variant ID or find first available
      const variantId = selectedVariantIds.value[product.id] || 
                       product.variants.find(variant => variant.available)?.id;
      if (!variantId) {
        console.error('No available variant for product:', product.title);
        return;
      }

      try {
        setLoading(product.id);
        recentlyAddedGWP.value = product.id;

        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            properties: { _gwp: 'true' }, // Mark as GWP product
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.description || 'Failed to add to cart');
        }

        await getCartAndUpdateGWP();
        recentlyAddedGWP.value = null;

      } catch (error) {
        console.error('Error adding GWP to cart:', error);
        recentlyAddedGWP.value = null;
      } finally {
        clearLoading();
      }
    };

    // Remove ALL free gifts if cart drops below minimum
    const removeGWPIfIneligible = async () => {
      // Only proceed if we're no longer eligible
      if (isEligibleForGWP.value) return;

      // Only proceed if there are items to remove
      if (cartItems.value.length === 0) return;

      // Find all free gift items
      const freeGiftItems = cartItems.value.filter(item => isFreeGift(item));

      if (freeGiftItems.length === 0) {
        return;
      }

      // Remove all free gifts
      for (const item of freeGiftItems) {
        try {
          await removeFromCart(item.key);
        } catch (error) {
          console.error('Error removing free gift:', item.title, error);
        }
      }

      // Refresh cart state
      await getCartAndUpdateGWP();
    };

    // Debounced version for watch
    const debouncedRemoveGWP = debounce(removeGWPIfIneligible, 300);

    // Watch cart total for changes
    watch(
      () => props.cartTotal,
      async (newTotal, oldTotal) => {
        const wasEligible = oldTotal >= gwpConfig.value.minimumPurchase;
        const isNowEligible = newTotal >= gwpConfig.value.minimumPurchase;

        // Cart dropped below minimum - remove free gifts
        if (wasEligible && !isNowEligible) {
          await debouncedRemoveGWP();
        }
      },
      { immediate: false }
    );

    // Auto-close accordion when ineligible
    watch(
      () => isEligibleForGWP.value,
      (isEligible) => {
        if (!isEligible && showGWP.value) {
          closeGWP();
        }
      }
    );

    // Accordion functions
    const handleGWPToggle = () => {
      if (isEligibleForGWP.value) {
        toggleGWP();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleGWPToggle();
      }
    };

    const handlePanelKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeGWP();
        const headerButton = document.getElementById('gwp-header');
        if (headerButton) {
          headerButton.focus();
        }
      }
    };

    // Expose for parent component
    const notifyGWPRemoved = (productId) => {
      addedGWP.value = addedGWP.value.filter(id => id !== productId);
    };
    onMounted(async () => {
      await getCartAndUpdateGWP();
    });

    return {
      gwpConfig,
      addedGWP,
      cartItems,
      isEligibleForGWP,
      hasSelectedGWP,
      availableGWPProducts,
      amountAwayMessage,
      productAlreadyAdded,
      addGWPToCart,
      removeGWPFromCart,
      isLoading,
      isAnyLoading,
      showGWP,
      handleGWPToggle,
      handleKeyDown,
      handlePanelKeyDown,
      notifyGWPRemoved,
      getCartAndUpdateGWP,
      getCroppedImageUrl,
      showAccordionIcons,
      modules,
      selectedVariantIds,
      remainingSelections,
    };
  },
};
</script>